import React, { useEffect, useRef, useState } from "react";
import {
  Users, MapPin, Boxes, MessageSquare, FileX, Plus, Trash2, Pencil,
  Upload, Download, CheckCircle2, AlertTriangle,
  X, Clock, User as UserIcon, Activity, Search, MoreVertical, Power, PowerOff,
  ShieldAlert, Lock, Timer,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import {
  listLocations, createLocation, updateLocation, deleteLocation,
  listUsers, createUser, updateUser, deleteUser, setUserStatus,
  listParts, createPart, updatePart, deletePart,
  partsMeta, partsSampleTemplate, partsUploadPreview, partsUploadConfirm,
  listTemplates, createTemplate, updateTemplate, deleteTemplate,
  listDeletedOrders, listActivityLogs,
  adminResetOrderData,
  listModulePermissions, updateModulePermissions,
  locTatSettings, locUpdateTatSettings,
} from "@/lib/api";
import { ROLE_LABELS, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TABS = [
  { key: "users", label: "Users", icon: Users },
  { key: "module_access", label: "Module Access", icon: Lock },
  { key: "locations", label: "Locations", icon: MapPin },
  { key: "parts", label: "Parts Master", icon: Boxes },
  { key: "templates", label: "WhatsApp Templates", icon: MessageSquare },
  { key: "loc_tat", label: "LOC Settings", icon: Timer },
  { key: "activity", label: "Activity Log", icon: Activity },
  { key: "deleted", label: "Deleted Audit Log", icon: FileX },
];

const Admin = () => {
  const { currentUser, refreshUsers, refreshLocations } = useApp();
  const [active, setActive] = useState("users");

  if (currentUser?.role !== "admin") {
    return (
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-12 text-center" data-testid="admin-restricted">
        <div className="text-[16px] font-semibold text-[#1a1b22]">Restricted</div>
        <div className="text-[13px] text-[#747684] mt-2">
          Admin section is only accessible to Admin role. Switch role from the top-right user menu.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-page">
      <div>
        <h1 className="text-[30px] font-semibold text-yamaha-800 leading-tight">Admin</h1>
        <p className="text-[14px] text-[#444653] mt-1">
          System administration — users, locations, parts master, WhatsApp templates and deleted order audit log.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-[#e2e1eb] overflow-hidden">
        <div className="flex border-b border-[#e2e1eb] overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold border-b-2 whitespace-nowrap ${
                active === t.key
                  ? "border-yamaha-700 text-yamaha-700 bg-yamaha-50"
                  : "border-transparent text-[#444653] hover:bg-[#f4f3fc]"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-5">
          {active === "users" && <UsersTab refreshUsers={refreshUsers} />}
          {active === "module_access" && <ModuleAccessTab />}
          {active === "locations" && <LocationsTab refreshLocations={refreshLocations} />}
          {active === "parts" && <PartsTab />}
          {active === "templates" && <TemplatesTab />}
          {active === "loc_tat" && <LocTatTab />}
          {active === "activity" && <ActivityLogTab />}
          {active === "deleted" && <DeletedTab />}
        </div>
      </div>

      <DangerZone />
    </div>
  );
};

// ============== DANGER ZONE ==============
const DangerZone = () => {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const handleReset = async () => {
    if (phrase !== "RESET") {
      toast.error("Please type RESET exactly to confirm.");
      return;
    }
    setBusy(true);
    try {
      const res = await adminResetOrderData(phrase);
      toast.success(
        `Order data cleared — ${res.orders_deleted} orders, ${res.deleted_orders_purged} deletion snapshots, ${res.order_activity_logs_purged} activity logs purged.`
      );
      setOpen(false);
      setPhrase("");
      // refresh app counts that may be cached
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-danger-zone"
      className="bg-white rounded-xl border-2 border-rose-200 overflow-hidden"
    >
      <div className="px-5 py-3 bg-rose-50 border-b border-rose-200 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-rose-600" />
        <span className="text-[13px] font-semibold text-rose-700 uppercase tracking-wider">
          Danger Zone
        </span>
      </div>
      <div className="p-5 flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="text-[14px] font-semibold text-[#1a1b22]">
            Reset Order Data
          </div>
          <p className="text-[12.5px] text-[#444653] mt-1 leading-relaxed">
            Permanently deletes <span className="font-semibold">all orders</span>{" "}
            (including drafts, completed, cancelled), their deletion audit
            snapshots, and order-related activity logs.{" "}
            <span className="font-semibold text-rose-700">
              This cannot be undone.
            </span>
            <br />
            <span className="text-[#747684]">
              Users, locations, parts master and WhatsApp templates remain
              untouched. Order numbering (IVA-XXX-YYYY-NNNN) restarts from 0001.
            </span>
          </p>
        </div>
        <Button
          data-testid="open-reset-order-data-btn"
          variant="destructive"
          onClick={() => setOpen(true)}
          className="bg-rose-600 hover:bg-rose-700"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Reset Order Data
        </Button>
      </div>

      <AlertDialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setPhrase(""); } }}>
        <AlertDialogContent data-testid="reset-order-data-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              Permanently delete all order data?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px]">
                <p>
                  This will delete every order on{" "}
                  <span className="font-semibold">IVA Motors — Service Sync</span>{" "}
                  including drafts, completed, returned and cancelled records,
                  along with the deletion audit snapshots and order-related
                  activity log entries.
                </p>
                <p className="text-rose-700 font-semibold">
                  Users, locations, parts master and WhatsApp templates will be
                  preserved.
                </p>
                <div>
                  <label className="text-[12px] font-semibold text-[#1a1b22] block mb-1">
                    Type <span className="font-mono bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">RESET</span> below to confirm
                  </label>
                  <input
                    data-testid="reset-confirm-input"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder="RESET"
                    autoFocus
                    className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] font-mono uppercase focus:outline-none focus:border-rose-500"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-btn" disabled={busy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-confirm-btn"
              onClick={(e) => { e.preventDefault(); handleReset(); }}
              disabled={busy || phrase !== "RESET"}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {busy ? "Deleting…" : "Yes, delete all orders"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== USERS ==============
const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  inactive: "bg-rose-50 text-rose-700 border border-rose-200",
};

const UsersTab = ({ refreshUsers }) => {
  const { currentUser } = useApp();
  const [list, setList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);
  const [statusConfirm, setStatusConfirm] = useState(null); // { user, nextActive }

  const load = async () => {
    setList(await listUsers());
    setLocations(await listLocations());
  };
  useEffect(() => { load(); }, []);

  const actorParams = () => ({ actor: currentUser?.name, actor_role: currentUser?.role });

  const resetPassword = async (u) => {
    const pwd = window.prompt(`Enter a new temporary password for ${u.name}:`, "iva@123");
    if (!pwd) return;
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      const { authAdminResetPassword } = await import("@/lib/api");
      await authAdminResetPassword(u.id, pwd);
      toast.success(`Password reset for ${u.name}. They must change it on next login.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const save = async (data) => {
    try {
      if (editing.id) await updateUser(editing.id, data, actorParams());
      else await createUser(data, actorParams());
      toast.success("Saved");
      setEditing(null);
      await load();
      await refreshUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmStatus = async () => {
    if (!statusConfirm) return;
    const { user, nextActive } = statusConfirm;
    try {
      await setUserStatus(user.id, {
        active: nextActive,
        actor: currentUser?.name,
        actor_role: currentUser?.role,
      });
      toast.success(nextActive ? `${user.name} reactivated` : `${user.name} marked Inactive`);
      setStatusConfirm(null);
      await load();
      await refreshUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? Their historical orders and audit entries will be preserved.`)) return;
    await deleteUser(u.id, actorParams());
    toast.success("Deleted");
    await load();
    await refreshUsers();
  };

  return (
    <div data-testid="users-tab">
      <div className="flex justify-between items-center mb-3">
        <div className="text-[13px] text-[#747684]">{list.length} user(s)</div>
        <Button
          data-testid="add-user-btn"
          onClick={() => setEditing({ name: "", mobile: "", role: "spare_executive", location_codes: [], active: true })}
          className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
        >
          <Plus className="w-4 h-4 mr-1" />Add User
        </Button>
      </div>
      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
        <table className="w-full text-[13px]" data-testid="users-table">
          <thead>
            <tr className="bg-[#f4f3fc]">
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Mobile</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Locations</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e1eb]">
            {list.map((u) => {
              const isActive = u.active !== false;
              const isSelf = currentUser && u.id === currentUser.id;
              return (
                <tr key={u.id} className={isActive ? "" : "bg-rose-50/30"} data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-semibold">{u.name}</td>
                  <td className="px-3 py-2 text-[#444653] font-mono text-[12px]">{u.mobile || u.email || "—"}</td>
                  <td className="px-3 py-2">{ROLE_LABELS[u.role]}</td>
                  <td className="px-3 py-2 text-[12px]">{u.location_codes.join(", ") || "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      data-testid={`user-status-${u.id}`}
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${STATUS_BADGE[isActive ? "active" : "inactive"]}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`user-more-${u.id}`}
                          className="p-1.5 hover:bg-[#f4f3fc] rounded inline-flex items-center"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-2" /> Edit User
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => resetPassword(u)} data-testid={`user-reset-pwd-${u.id}`}>
                          <Power className="w-3.5 h-3.5 mr-2" /> Reset Password
                        </DropdownMenuItem>
                        {isActive ? (
                          <DropdownMenuItem
                            disabled={isSelf}
                            onClick={() => !isSelf && setStatusConfirm({ user: u, nextActive: false })}
                            data-testid={`user-deactivate-${u.id}`}
                            className="text-rose-600 focus:text-rose-600"
                          >
                            <PowerOff className="w-3.5 h-3.5 mr-2" />
                            Mark Inactive
                            {isSelf && <span className="ml-1 text-[10px] text-[#747684]">(self)</span>}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => setStatusConfirm({ user: u, nextActive: true })}
                            data-testid={`user-activate-${u.id}`}
                            className="text-emerald-700 focus:text-emerald-700"
                          >
                            <Power className="w-3.5 h-3.5 mr-2" /> Mark Active
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => remove(u)}
                          data-testid={`user-delete-${u.id}`}
                          className="text-red-600 focus:text-red-600"
                          disabled={isSelf}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditDialog title={editing.id ? "Edit User" : "Add User"} onClose={() => setEditing(null)} onSave={() => save(editing)}>
          <Input label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="user-name" />
          <Input
            label="Mobile Number *"
            value={editing.mobile || ""}
            onChange={(v) => setEditing({ ...editing, mobile: v.replace(/\D/g, "").slice(0, 10) })}
            testid="user-mobile"
            placeholder="10-digit mobile"
          />
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Role</label>
            <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="input mt-1" data-testid="user-role">
              <option value="spare_executive">Parts Executive</option>
              <option value="parts_manager">Parts Manager</option>
              <option value="crm">CRM</option>
              <option value="floor_supervisor">Floor Supervisor</option>
              <option value="works_manager">Works Manager</option>
              <option value="billing_team">Billing Team</option>
              <option value="service_advisor">Service Advisor</option>
              <option value="workshop_manager">Workshop Manager</option>
              <option value="service_manager">Service Manager</option>
              <option value="security">Security</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Account Status</label>
            <div className="mt-1.5 flex gap-2" data-testid="user-status-field">
              {[
                { val: true, label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
                { val: false, label: "Inactive", cls: "bg-rose-50 text-rose-700 border-rose-300" },
              ].map((opt) => {
                const selected = (editing.active !== false) === opt.val;
                return (
                  <button
                    key={String(opt.val)}
                    type="button"
                    data-testid={`user-status-${opt.label.toLowerCase()}-btn`}
                    onClick={() => setEditing({ ...editing, active: opt.val })}
                    className={`px-3 py-1.5 rounded-full text-[12px] border font-semibold ${
                      selected ? opt.cls : "bg-white text-[#747684] border-[#e2e1eb]"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10.5px] text-[#747684] mt-1.5">
              Inactive users cannot login or access any module. Their historical orders and audit entries are preserved.
            </p>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Locations (multi-select)</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {locations.map((l) => {
                const checked = editing.location_codes.includes(l.code);
                return (
                  <button
                    key={l.code}
                    data-testid={`user-loc-${l.code}`}
                    onClick={() =>
                      setEditing({
                        ...editing,
                        location_codes: checked
                          ? editing.location_codes.filter((c) => c !== l.code)
                          : [...editing.location_codes, l.code],
                      })
                    }
                    className={`px-3 py-1 rounded-full text-[12px] border ${
                      checked ? "bg-yamaha-700 text-white border-yamaha-700" : "bg-white text-[#444653] border-[#e2e1eb]"
                    }`}
                  >
                    {l.code} · {l.name}
                  </button>
                );
              })}
            </div>
          </div>
        </EditDialog>
      )}

      <AlertDialog open={!!statusConfirm} onOpenChange={(o) => { if (!o) setStatusConfirm(null); }}>
        <AlertDialogContent data-testid="user-status-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusConfirm?.nextActive ? "Reactivate User Account" : "Deactivate User Account"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusConfirm?.nextActive ? (
                <>
                  <span className="font-semibold text-[#1a1b22]">{statusConfirm?.user?.name}</span>{" "}
                  will regain access to the app. They can resume creating, editing, and viewing orders immediately.
                </>
              ) : (
                <>
                  <span className="font-semibold text-[#1a1b22]">{statusConfirm?.user?.name}</span>{" "}
                  will no longer be able to login or access any module until reactivated. Existing sessions will be ejected on next refresh. Their orders, drafts, timeline entries, and audit logs are preserved.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-status-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-status-confirm-btn"
              onClick={confirmStatus}
              className={statusConfirm?.nextActive ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
            >
              {statusConfirm?.nextActive ? "Reactivate" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== LOCATIONS ==============
const LocationsTab = ({ refreshLocations }) => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => setList(await listLocations());
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing.id) await updateLocation(editing.id, { code: editing.code, name: editing.name, active: editing.active });
      else await createLocation({ code: editing.code, name: editing.name, active: editing.active });
      toast.success("Saved");
      setEditing(null);
      await load();
      await refreshLocations();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete location?")) return;
    await deleteLocation(id); toast.success("Deleted"); await load(); await refreshLocations();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-[13px] text-[#747684]">{list.length} location(s)</div>
        <Button data-testid="add-location-btn" onClick={() => setEditing({ code: "", name: "", active: true })} className="bg-yamaha-700 hover:bg-yamaha-800 text-white">
          <Plus className="w-4 h-4 mr-1" />Add Location
        </Button>
      </div>
      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
        <table className="w-full text-[13px]">
          <thead><tr className="bg-[#f4f3fc]">
            <th className="px-3 py-2 text-left">Code</th>
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Active</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-[#e2e1eb]">
            {list.map((l) => (
              <tr key={l.id}>
                <td className="px-3 py-2 font-mono text-yamaha-700 font-semibold">{l.code}</td>
                <td className="px-3 py-2 font-semibold">{l.name}</td>
                <td className="px-3 py-2">{l.active ? "Yes" : "No"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing(l)} className="p-1.5 hover:bg-[#f4f3fc] rounded" data-testid={`edit-location-${l.code}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(l.id)} className="p-1.5 hover:bg-[#f4f3fc] rounded text-red-600" data-testid={`delete-location-${l.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <EditDialog title={editing.id ? "Edit Location" : "Add Location"} onClose={() => setEditing(null)} onSave={save}>
          <Input label="Code *" value={editing.code} onChange={(v) => setEditing({ ...editing, code: v.toUpperCase() })} testid="loc-code" />
          <Input label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="loc-name" />
          <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
        </EditDialog>
      )}
    </div>
  );
};

// ============== PARTS ==============
const PartsTab = () => {
  const { currentUser } = useApp();
  const role = currentUser?.role;
  const canUpload = role === "admin" || role === "parts_manager";
  const canEdit = canUpload;

  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [meta, setMeta] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, counts, filename }
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => setList(await listParts(q, 1000));
  const loadMeta = async () => setMeta(await partsMeta());
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { loadMeta(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Only .xlsx files are supported");
      return;
    }
    setUploading(true);
    try {
      const data = await partsUploadPreview(file);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadSample = async () => {
    try {
      const resp = await partsSampleTemplate();
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "IVA_PARTS_MASTER_TEMPLATE.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sample template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  const confirmUpload = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const body = {
        rows: preview.rows.map((r) => ({
          part_number: r.part_number,
          part_name: r.part_name,
          action: r.action,
        })),
        actor: currentUser?.name,
        actor_role: currentUser?.role,
        filename: preview.filename,
      };
      const summary = await partsUploadConfirm(body);
      toast.success(
        `Created: ${summary.created} · Updated: ${summary.updated} · Skipped: ${summary.skipped}${summary.failed ? ` · Failed: ${summary.failed}` : ""}`
      );
      setPreview(null);
      await Promise.all([load(), loadMeta()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
    } finally {
      setConfirming(false);
    }
  };

  const save = async () => {
    try {
      if (editing.id) await updatePart(editing.id, editing);
      else await createPart(editing);
      toast.success("Saved"); setEditing(null); load(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete part?")) return;
    await deletePart(id); toast.success("Deleted"); load(); loadMeta();
  };

  return (
    <div className="space-y-4">
      {/* Bulk Upload Card */}
      <div className="rounded-xl border border-[#e2e1eb] bg-white overflow-hidden" data-testid="parts-bulk-card">
        <div className="px-4 py-3 border-b border-[#e2e1eb] bg-[#faf8ff] flex items-center justify-between">
          <div>
            <h3 className="text-[14px] font-semibold text-[#1a1b22] flex items-center gap-2">
              <Upload className="w-4 h-4 text-yamaha-700" /> Bulk Upload Parts Master
            </h3>
            <p className="text-[11.5px] text-[#747684] mt-0.5">
              Upload an Excel file (.xlsx) with two columns — <b>Part Name</b> and <b>Part Number</b> — to bulk create or update parts.
            </p>
          </div>
          {meta && (
            <div className="text-right text-[10.5px] text-[#747684] leading-snug">
              <div>Total parts: <b className="text-[#1a1b22]">{meta.total_parts}</b></div>
              {meta.last_uploaded_at && (
                <>
                  <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Last updated: {fmtDateTime(meta.last_uploaded_at)}</div>
                  <div className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> By: {meta.last_uploaded_by || "—"}</div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="p-4 grid md:grid-cols-3 gap-3">
          <div
            onDragOver={(e) => { if (canUpload) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (!canUpload) return toast.error("You do not have permission to upload");
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            className={`md:col-span-2 border-2 border-dashed rounded-lg p-5 flex flex-col items-center justify-center text-center transition-colors ${
              dragOver ? "border-yamaha-700 bg-yamaha-50" : "border-[#c4c5d5] bg-[#fafbff]"
            } ${!canUpload ? "opacity-60" : ""}`}
            data-testid="parts-drop-zone"
          >
            <Upload className="w-7 h-7 text-yamaha-700 mb-2" />
            <div className="text-[13px] font-semibold text-[#1a1b22]">
              Drag &amp; drop your .xlsx file here
            </div>
            <div className="text-[11.5px] text-[#747684] mt-1">
              or
            </div>
            <button
              data-testid="upload-btn"
              disabled={!canUpload || uploading}
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 px-4 py-2 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "Reading file…" : "Choose Excel File"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="parts-file-input"
            />
            {!canUpload && (
              <div className="text-[10.5px] text-rose-600 mt-2 font-semibold">
                {role === "parts_executive" ? "Read-only access — only Admin and Parts Manager can upload." : "Switch to Admin / Parts Manager role to upload."}
              </div>
            )}
          </div>
          <div className="bg-[#faf8ff] rounded-lg p-4 border border-[#e2e1eb] flex flex-col">
            <div className="text-[12px] font-semibold text-[#1a1b22] mb-1">Need the format?</div>
            <p className="text-[11px] text-[#747684] flex-1">
              Download our sample template containing the exact column headers and 3 example rows.
            </p>
            <button
              data-testid="download-sample-btn"
              onClick={downloadSample}
              className="mt-3 px-3 py-2 rounded-md border border-[#e2e1eb] bg-white hover:bg-yamaha-50 text-[12px] font-semibold text-yamaha-700 inline-flex items-center justify-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> Download Sample Template
            </button>
          </div>
        </div>
      </div>

      {/* Search + Add */}
      <div className="flex justify-between items-center gap-3">
        <input
          data-testid="parts-search"
          placeholder="Search parts by name or number..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="input w-80 !py-1.5"
        />
        {canEdit && (
          <Button
            data-testid="add-part-btn"
            onClick={() => setEditing({ part_number: "", part_name: "", active: true })}
            className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
          >
            <Plus className="w-4 h-4 mr-1" />Add Part
          </Button>
        )}
      </div>

      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden max-h-[55vh] overflow-y-auto bg-white">
        <table className="w-full text-[13px]">
          <thead><tr className="bg-[#fafbff] border-b border-[#e2e1eb] text-[10px] uppercase tracking-wider text-[#747684]">
            <th className="px-3 py-2 text-left font-bold">Part Number</th>
            <th className="px-3 py-2 text-left font-bold">Part Name</th>
            {canEdit && <th className="px-3 py-2 text-right font-bold">Actions</th>}
          </tr></thead>
          <tbody className="divide-y divide-[#f1f0f7]">
            {list.map((p) => (
              <tr key={p.id} className="hover:bg-[#faf8ff]">
                <td className="px-3 py-2 font-mono text-[12px] text-yamaha-700">{p.part_number}</td>
                <td className="px-3 py-2 text-[#1a1b22]">{p.part_name}</td>
                {canEdit && (
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-[#f4f3fc] rounded"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-[#f4f3fc] rounded text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                )}
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={canEdit ? 3 : 2} className="px-3 py-8 text-center text-[12.5px] text-[#747684]">No parts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditDialog title={editing.id ? "Edit Part" : "Add Part"} onClose={() => setEditing(null)} onSave={save}>
          <Input label="Part Number *" value={editing.part_number} onChange={(v) => setEditing({ ...editing, part_number: v.toUpperCase() })} testid="part-number" />
          <Input label="Part Name *" value={editing.part_name} onChange={(v) => setEditing({ ...editing, part_name: v })} testid="part-name" />
        </EditDialog>
      )}

      {preview && (
        <PreviewDialog
          preview={preview}
          confirming={confirming}
          onClose={() => setPreview(null)}
          onConfirm={confirmUpload}
        />
      )}
    </div>
  );
};

const PreviewDialog = ({ preview, confirming, onClose, onConfirm }) => {
  const ACTION_CLS = {
    new: "bg-emerald-50 text-emerald-700",
    update: "bg-blue-50 text-blue-700",
    skipped: "bg-[#e2e1eb] text-[#444653]",
    failed: "bg-rose-50 text-rose-700",
  };
  const ACTION_LABEL = { new: "New", update: "Update", skipped: "Skipped", failed: "Failed" };
  const total = preview.rows.length;
  const c = preview.counts || {};
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" data-testid="parts-preview-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#e2e1eb] flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1a1b22]">Preview Upload — {preview.filename}</h3>
            <p className="text-[11.5px] text-[#747684] mt-0.5">Review the changes below, then confirm to apply.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#f4f3fc]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-[#e2e1eb] bg-[#faf8ff] grid grid-cols-4 gap-2 text-[11.5px]" data-testid="parts-preview-summary">
          <SumPill icon={CheckCircle2} label="New" value={c.new || 0} cls="text-emerald-700 bg-emerald-50" />
          <SumPill icon={Pencil} label="Update" value={c.update || 0} cls="text-blue-700 bg-blue-50" />
          <SumPill icon={Clock} label="Skipped" value={c.skipped || 0} cls="text-[#444653] bg-[#e2e1eb]" />
          <SumPill icon={AlertTriangle} label="Failed" value={c.failed || 0} cls="text-rose-700 bg-rose-50" />
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="bg-[#fafbff] border-b border-[#e2e1eb] text-[10px] uppercase tracking-wider text-[#747684]">
                <th className="px-4 py-2 text-left font-bold w-10">#</th>
                <th className="px-4 py-2 text-left font-bold">Part Number</th>
                <th className="px-4 py-2 text-left font-bold">Part Name</th>
                <th className="px-4 py-2 text-left font-bold">Action</th>
                <th className="px-4 py-2 text-left font-bold">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f0f7]">
              {preview.rows.map((r, i) => (
                <tr key={`${r.row_no}-${r.part_number || i}`} className="hover:bg-[#faf8ff]" data-testid={`preview-row-${i}`}>
                  <td className="px-4 py-1.5 text-[#a4a6b3]">{r.row_no}</td>
                  <td className="px-4 py-1.5 font-mono text-yamaha-700">{r.part_number || <span className="text-rose-600">—</span>}</td>
                  <td className="px-4 py-1.5 text-[#1a1b22]">{r.part_name || <span className="text-rose-600">—</span>}</td>
                  <td className="px-4 py-1.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${ACTION_CLS[r.action]}`}>
                      {ACTION_LABEL[r.action]}
                    </span>
                  </td>
                  <td className="px-4 py-1.5 text-[11px] text-[#747684]">{r.reason || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-[#e2e1eb] flex items-center justify-between bg-white">
          <div className="text-[11.5px] text-[#747684]">{total} row(s) parsed</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={confirming} data-testid="preview-cancel">Cancel</Button>
            <Button
              onClick={onConfirm}
              disabled={confirming || total === 0}
              className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
              data-testid="preview-confirm"
            >
              {confirming ? "Applying…" : "Confirm Upload"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SumPill = ({ icon: Icon, label, value, cls }) => (
  <div className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 ${cls}`}>
    <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[10px]">
      <Icon className="w-3 h-3" /> {label}
    </span>
    <span className="text-[14px] font-semibold">{value}</span>
  </div>
);

// ============== TEMPLATES ==============
const TemplatesTab = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setList(await listTemplates());
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing.id) await updateTemplate(editing.id, editing);
      else await createTemplate(editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await deleteTemplate(id); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-[13px] text-[#747684]">{list.length} template(s)</div>
        <Button data-testid="add-template-btn" onClick={() => setEditing({ code: "", name: "", body: "", active: true })} className="bg-yamaha-700 hover:bg-yamaha-800 text-white">
          <Plus className="w-4 h-4 mr-1" />Add Template
        </Button>
      </div>
      <div className="space-y-2">
        {list.map((t) => (
          <div key={t.id} className="border border-[#e2e1eb] rounded-lg p-4 bg-[#faf8ff]">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[13px] font-semibold text-yamaha-700">{t.name} <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-yamaha-100 text-yamaha-700 font-mono">{t.code}</span></div>
                <div className="text-[12px] text-[#444653] mt-1 whitespace-pre-wrap">{t.body}</div>
              </div>
              <div>
                <button onClick={() => setEditing(t)} className="p-1.5 hover:bg-white rounded"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(t.id)} className="p-1.5 hover:bg-white rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <EditDialog title={editing.id ? "Edit Template" : "Add Template"} onClose={() => setEditing(null)} onSave={save}>
          <Input label="Code *" value={editing.code} onChange={(v) => setEditing({ ...editing, code: v.toUpperCase() })} testid="template-code" />
          <Input label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="template-name" />
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Body</label>
            <textarea data-testid="template-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows="5" className="input mt-1" placeholder="Use {customer_name}, {order_number}, {edd}" />
          </div>
        </EditDialog>
      )}
    </div>
  );
};

// ============== ACTIVITY LOG ==============
const ACTION_LABELS = {
  ORDER_CREATED: "Created Order",
  DRAFT_SAVED: "Saved Draft",
  DRAFT_SUBMITTED: "Submitted Draft",
  ORDER_UPDATED: "Updated Order",
  MARKED_ORDERED: "Marked Ordered",
  RECEIVED_PARTS: "Received Parts",
  PARTIAL_RECEIPT: "Received Parts (Partial)",
  BACKORDERED: "Marked Backorder",
  EDD_UPDATED: "Updated EDD",
  ORDER_COMPLETED: "Completed / Invoiced",
  REMINDER_SENT: "Sent Reminder",
  CNR_LOGGED: "Logged Customer-Not-Responding",
  UNCLAIMED: "Marked Unclaimed",
  RETURNED_TO_STOCK: "Returned To Stock",
  ORDER_CANCELLED: "Cancelled Order",
  ORDER_DELETED: "Deleted Order",
  PARTS_MASTER_BULK_UPLOAD: "Parts Master Bulk Upload",
  USER_CREATED: "Created User",
  USER_UPDATED: "Updated User",
  USER_DELETED: "Deleted User",
  USER_ACTIVATED: "Reactivated User",
  USER_DEACTIVATED: "Deactivated User",
  USER_LOGIN: "User Login",
  USER_LOGIN_FAILED: "Login Failed",
  USER_LOGOUT: "User Logout",
  USER_PASSWORD_CHANGED: "Password Changed",
  USER_PASSWORD_RESET_BY_ADMIN: "Password Reset by Admin",
  USER_LOCKED: "Account Locked",
};

const ACTION_COLORS = {
  ORDER_CREATED: "bg-blue-50 text-blue-700 border border-blue-100",
  DRAFT_SAVED: "bg-slate-50 text-slate-700 border border-slate-200",
  DRAFT_SUBMITTED: "bg-blue-50 text-blue-700 border border-blue-100",
  ORDER_UPDATED: "bg-yamaha-50 text-yamaha-700 border border-yamaha-100",
  MARKED_ORDERED: "bg-amber-50 text-amber-800 border border-amber-100",
  RECEIVED_PARTS: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  PARTIAL_RECEIPT: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  BACKORDERED: "bg-orange-50 text-orange-700 border border-orange-100",
  EDD_UPDATED: "bg-amber-50 text-amber-800 border border-amber-100",
  ORDER_COMPLETED: "bg-green-50 text-green-800 border border-green-100",
  REMINDER_SENT: "bg-purple-50 text-purple-700 border border-purple-100",
  CNR_LOGGED: "bg-purple-50 text-purple-700 border border-purple-100",
  UNCLAIMED: "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100",
  RETURNED_TO_STOCK: "bg-zinc-100 text-zinc-700 border border-zinc-200",
  ORDER_CANCELLED: "bg-rose-50 text-rose-700 border border-rose-100",
  ORDER_DELETED: "bg-rose-50 text-rose-700 border border-rose-100",
  PARTS_MASTER_BULK_UPLOAD: "bg-yamaha-50 text-yamaha-700 border border-yamaha-100",
  USER_CREATED: "bg-blue-50 text-blue-700 border border-blue-100",
  USER_UPDATED: "bg-yamaha-50 text-yamaha-700 border border-yamaha-100",
  USER_DELETED: "bg-rose-50 text-rose-700 border border-rose-100",
  USER_ACTIVATED: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  USER_DEACTIVATED: "bg-rose-50 text-rose-700 border border-rose-100",
  USER_LOGIN: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  USER_LOGIN_FAILED: "bg-amber-50 text-amber-800 border border-amber-100",
  USER_LOGOUT: "bg-slate-100 text-slate-700 border border-slate-200",
  USER_PASSWORD_CHANGED: "bg-yamaha-50 text-yamaha-700 border border-yamaha-100",
  USER_PASSWORD_RESET_BY_ADMIN: "bg-yamaha-50 text-yamaha-700 border border-yamaha-100",
  USER_LOCKED: "bg-rose-50 text-rose-700 border border-rose-100",
};

const ActivityLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    q: "",
    actor: "ALL",
    actor_role: "ALL",
    action: "ALL",
    from_date: "",
    to_date: "",
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filters.q) params.q = filters.q;
      if (filters.actor !== "ALL") params.actor = filters.actor;
      if (filters.actor_role !== "ALL") params.actor_role = filters.actor_role;
      if (filters.action !== "ALL") params.action = filters.action;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const res = await listActivityLogs(params);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Could not load activity log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => setUsers(await listUsers()))();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = () => load();
  const onReset = () => {
    setFilters({ q: "", actor: "ALL", actor_role: "ALL", action: "ALL", from_date: "", to_date: "" });
    setTimeout(load, 50);
  };

  const downloadCsv = () => {
    const headers = ["Date/Time", "User", "Role", "Action", "Target", "Location", "Details"];
    const rows = logs.map((l) => [
      fmtDateTime(l.at),
      l.actor || "",
      ROLE_LABELS[l.actor_role] || l.actor_role || "",
      ACTION_LABELS[l.action] || l.action,
      l.target_label || "",
      l.location_code || "",
      (l.details || "").replace(/"/g, '""'),
    ]);
    const csv =
      [headers, ...rows]
        .map((r) => r.map((c) => `"${String(c ?? "")}"`).join(","))
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="activity-log-tab" className="space-y-4">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[11px] font-semibold uppercase text-[#747684]">Search</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#747684]" />
            <input
              data-testid="activity-search"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Order #, user, details..."
              className="input mt-1 pl-8"
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase text-[#747684]">User</label>
          <select
            data-testid="activity-user"
            value={filters.actor}
            onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
            className="input mt-1"
          >
            <option value="ALL">All users</option>
            {users.map((u) => (
              <option key={u.id} value={u.name}>{u.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase text-[#747684]">Role</label>
          <select
            data-testid="activity-role"
            value={filters.actor_role}
            onChange={(e) => setFilters({ ...filters, actor_role: e.target.value })}
            className="input mt-1"
          >
            <option value="ALL">All roles</option>
            <option value="spare_executive">Spare Executive</option>
            <option value="parts_manager">Parts Manager</option>
            <option value="crm">CRM</option>
            <option value="floor_supervisor">Floor Supervisor</option>
            <option value="works_manager">Works Manager</option>
            <option value="billing_team">Billing Team</option>
            <option value="admin">Admin</option>
            <option value="system">System</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase text-[#747684]">Action</label>
          <select
            data-testid="activity-action"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="input mt-1"
          >
            <option value="ALL">All actions</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase text-[#747684]">From</label>
          <input
            type="date"
            data-testid="activity-from"
            value={filters.from_date}
            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            className="input mt-1"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase text-[#747684]">To</label>
          <input
            type="date"
            data-testid="activity-to"
            value={filters.to_date}
            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            className="input mt-1"
          />
        </div>
        <Button
          data-testid="activity-apply-btn"
          onClick={onApply}
          className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
        >
          Apply
        </Button>
        <Button data-testid="activity-reset-btn" variant="outline" onClick={onReset}>
          Reset
        </Button>
        <Button
          data-testid="activity-export-btn"
          variant="outline"
          onClick={downloadCsv}
          disabled={logs.length === 0}
        >
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="text-[12px] text-[#747684]" data-testid="activity-total">
        {loading ? "Loading..." : `Showing ${logs.length} of ${total} entries`}
      </div>

      <div className="border border-[#e2e1eb] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#faf8ff] text-[#444653] uppercase text-[10.5px] tracking-wider">
              <tr>
                <th className="px-4 py-2.5 text-left">Date / Time</th>
                <th className="px-4 py-2.5 text-left">User</th>
                <th className="px-4 py-2.5 text-left">Role</th>
                <th className="px-4 py-2.5 text-left">Action</th>
                <th className="px-4 py-2.5 text-left">Target</th>
                <th className="px-4 py-2.5 text-left">Location</th>
                <th className="px-4 py-2.5 text-left">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e1eb] bg-white">
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-[#747684]">
                    No activity yet. Perform actions across the app to populate the audit log.
                  </td>
                </tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="hover:bg-[#faf8ff]" data-testid={`activity-row-${l.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-[#444653]">{fmtDateTime(l.at)}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-medium text-[#1a1b22]">{l.actor || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-[#747684]">{ROLE_LABELS[l.actor_role] || l.actor_role || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${ACTION_COLORS[l.action] || "bg-slate-50 text-slate-700 border border-slate-200"}`}>
                      {ACTION_LABELS[l.action] || l.action}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-[#1a1b22] font-medium">{l.target_label || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-[#444653]">{l.location_code || "—"}</td>
                  <td className="px-4 py-2 text-[#444653] max-w-[320px] truncate" title={l.details || ""}>{l.details || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== DELETED ==============
const DeletedTab = () => {
  const [list, setList] = useState([]);
  useEffect(() => { (async () => setList(await listDeletedOrders()))(); }, []);
  return (
    <div>
      <div className="text-[13px] text-[#747684] mb-3">{list.length} record(s) — system-maintained audit log.</div>
      {list.length === 0 ? (
        <div className="text-center py-10 text-[#747684] text-[13px]">No deleted orders.</div>
      ) : (
        <div className="space-y-2">
          {list.map((d) => (
            <div key={d.id} className="border border-[#e2e1eb] rounded-lg p-4 bg-rose-50/40">
              <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold">{d.order_snapshot?.order_number} · {d.order_snapshot?.customer_name}</div>
                <div className="text-[11px] text-[#747684]">{fmtDateTime(d.deleted_at)}</div>
              </div>
              <div className="text-[12px] text-[#444653] mt-1">
                Deleted by <b>{d.deleted_by || "-"}</b> ({d.deleted_by_role || "-"})
              </div>
              <div className="text-[12px] mt-1">Model: {d.order_snapshot?.model_name} · {d.order_snapshot?.parts?.length || 0} parts</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== helpers ==============
const Input = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase text-[#747684]">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="input mt-1" />
  </div>
);

const EditDialog = ({ title, onClose, onSave, children }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="edit-dialog">
    <div className="bg-white rounded-xl border border-[#e2e1eb] p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-[16px] font-semibold">{title}</h3>
      <div className="space-y-3">{children}</div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="dialog-save-btn" onClick={onSave} className="bg-yamaha-700 hover:bg-yamaha-800 text-white">Save</Button>
      </div>
    </div>
  </div>
);


// =====================================================================
// Module Access — Admin → per-role permission matrix
// =====================================================================
const ROLE_LABEL = {
  admin: "Administrator",
  parts_manager: "Parts Manager",
  spare_executive: "Parts Executive",
  crm: "CRM",
  floor_supervisor: "Floor Supervisor",
  works_manager: "Works Manager",
  billing_team: "Billing Team",
  service_advisor: "Service Advisor",
  workshop_manager: "Workshop Manager",
  service_manager: "Service Manager",
  security: "Security",
};

const ModuleAccessTab = () => {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState(["view", "add", "edit", "delete"]);
  const [dirty, setDirty] = useState({}); // { role: {module: {action: bool}} }
  const [loading, setLoading] = useState(false);
  const [savingRole, setSavingRole] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await listModulePermissions();
      setRows(d.rows || []);
      setModules(d.modules || []);
      setActions(d.actions || ["view", "add", "edit", "delete"]);
      setDirty({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load module permissions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const effective = (role, mod, act) => {
    const overrides = dirty[role]?.[mod];
    if (overrides && act in overrides) return !!overrides[act];
    const row = rows.find((r) => r.role === role);
    return !!row?.permissions?.[mod]?.[act];
  };

  const toggle = (role, mod, act) => {
    if (role === "admin") return; // read-only
    setDirty((d) => {
      const next = { ...d };
      next[role] = { ...(next[role] || {}) };
      next[role][mod] = { ...(next[role][mod] || {}) };
      next[role][mod][act] = !effective(role, mod, act);
      return next;
    });
  };

  const hasChanges = (role) => Object.keys(dirty[role] || {}).length > 0;

  const save = async (role) => {
    setSavingRole(role);
    // Compose full matrix (current state + dirty overrides) so backend gets a complete map.
    const row = rows.find((r) => r.role === role);
    const perms = {};
    for (const m of modules) {
      perms[m.key] = {};
      for (const a of actions) perms[m.key][a] = effective(role, m.key, a);
    }
    try {
      const r = await updateModulePermissions(role, perms);
      toast.success(`Saved permissions for ${ROLE_LABEL[role] || role}`);
      // Reflect saved perms in local state
      setRows((rs) => rs.map((x) => x.role === role ? { ...x, permissions: r.permissions } : x));
      setDirty((d) => { const n = { ...d }; delete n[role]; return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingRole(null); }
  };

  const revert = (role) => setDirty((d) => { const n = { ...d }; delete n[role]; return n; });

  return (
    <div data-testid="module-access-tab" className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <Lock className="w-5 h-5 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-semibold text-[#1a1b22]">Module Access Matrix</div>
          <div className="text-[12.5px] text-[#747684] mt-0.5">
            Control which modules each role can View, Add, Edit or Delete records in.
            Admin always has full access. Changes apply on the user&rsquo;s next page load.
          </div>
        </div>
      </div>

      {loading && <div className="text-[12.5px] text-[#747684] py-6 text-center">Loading…</div>}

      {!loading && rows.map((row) => {
        const isAdmin = row.role === "admin";
        const changed = hasChanges(row.role);
        return (
          <div key={row.role} className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden"
               data-testid={`role-perm-card-${row.role}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2e1eb] bg-[#faf8ff]">
              <div>
                <div className="text-[13.5px] font-semibold text-[#1a1b22]">{ROLE_LABEL[row.role] || row.role}</div>
                {isAdmin && <div className="text-[11px] text-[#747684]">Full access (locked)</div>}
              </div>
              <div className="flex items-center gap-2">
                {changed && (
                  <button data-testid={`role-perm-revert-${row.role}`} onClick={() => revert(row.role)}
                    className="text-[12px] px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[#444653] hover:bg-white">
                    Revert
                  </button>
                )}
                <button data-testid={`role-perm-save-${row.role}`} disabled={isAdmin || !changed || savingRole === row.role}
                  onClick={() => save(row.role)}
                  className="text-[12px] px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                  {savingRole === row.role ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-white text-[10.5px] uppercase tracking-wider text-[#747684]">
                  <tr>
                    <th className="px-4 py-2 text-left">Module</th>
                    {actions.map((a) => <th key={a} className="px-4 py-2 text-center capitalize">{a}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#eeecf6]">
                  {modules.map((m) => (
                    <tr key={m.key} data-testid={`row-${row.role}-${m.key}`} className="hover:bg-[#faf8ff]">
                      <td className="px-4 py-2 font-semibold text-[#1a1b22]">{m.label}</td>
                      {actions.map((a) => {
                        const checked = effective(row.role, m.key, a);
                        return (
                          <td key={a} className="px-4 py-2 text-center">
                            <input type="checkbox" checked={checked}
                              disabled={isAdmin}
                              onChange={() => toggle(row.role, m.key, a)}
                              data-testid={`chk-${row.role}-${m.key}-${a}`}
                              className="w-4 h-4 accent-yamaha-700 disabled:opacity-40 disabled:cursor-not-allowed" />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const LocTatTab = () => {
  const [vehicleReady, setVehicleReady] = useState({ loc1_days: 5, loc2_days: 15, loc3_days: 30 });
  const [noWorkStarted, setNoWorkStarted] = useState({ loc1_days: 3, loc2_days: 10, loc3_days: 20 });
  const [workshopContact, setWorkshopContact] = useState("");
  const [parkingEnabled, setParkingEnabled] = useState(true);
  const [parkingPerDay, setParkingPerDay] = useState("");
  const [parkingGraceDays, setParkingGraceDays] = useState(5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await locTatSettings();
      setVehicleReady(d.vehicle_ready);
      setNoWorkStarted(d.no_work_started);
      setWorkshopContact(d.workshop_contact_number || "");
      setParkingEnabled(d.parking_enabled !== false);
      setParkingPerDay(d.parking_charge_per_day || "");
      setParkingGraceDays(d.parking_grace_period_days ?? 5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load TAT settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    for (const [label, s] of [["Vehicle Ready", vehicleReady], ["No Work Started", noWorkStarted]]) {
      if (!(s.loc1_days >= 1 && s.loc1_days < s.loc2_days && s.loc2_days < s.loc3_days)) {
        toast.error(`${label}: LOC1 < LOC2 < LOC3 day, and LOC1 day must be at least 1.`);
        return;
      }
    }
    setSaving(true);
    try {
      await locUpdateTatSettings({
        vehicle_ready: vehicleReady, no_work_started: noWorkStarted,
        workshop_contact_number: workshopContact, parking_charge_per_day: parkingPerDay,
        parking_enabled: parkingEnabled, parking_grace_period_days: parkingGraceDays,
      });
      toast.success("LOC settings saved.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => (d?.msg || "").replace(/^Value error, /, "")).filter(Boolean).join("; ")
        : (typeof detail === "string" ? detail : "");
      toast.error(msg || "Save failed");
    } finally { setSaving(false); }
  };

  const Block = ({ title, desc, state, setState, prefix }) => (
    <div className="bg-white border border-[#e2e1eb] rounded-xl p-4">
      <div className="text-[13.5px] font-semibold text-[#1a1b22]">{title}</div>
      <div className="text-[11.5px] text-[#747684] mt-0.5 mb-3">{desc}</div>
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((n) => (
          <div key={n}>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              LOC {n} — Day
            </label>
            <input
              type="number" min={1} data-testid={`${prefix}-loc${n}-days`}
              value={state[`loc${n}_days`]}
              onChange={(e) => setState((s) => ({ ...s, [`loc${n}_days`]: parseInt(e.target.value, 10) || 0 }))}
              className="w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400"
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="loc-tat-tab" className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <Timer className="w-5 h-5 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-semibold text-[#1a1b22]">LOC Tracker — Settings</div>
          <div className="text-[12.5px] text-[#747684] mt-0.5">
            TAT days from the base date (Vehicle Ready Date / LOC Trigger Date) after which each LOC letter becomes due, plus the contact number shown in LOC WhatsApp reminders.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[12.5px] text-[#747684] py-6 text-center">Loading…</div>
      ) : (
        <>
          <div className="bg-white border border-[#e2e1eb] rounded-xl p-4">
            <div className="text-[13.5px] font-semibold text-[#1a1b22]">Workshop Contact Number</div>
            <div className="text-[11.5px] text-[#747684] mt-0.5 mb-3">Used in every WhatsApp reminder, across all locations.</div>
            <input
              data-testid="loc-tat-workshop-contact" value={workshopContact}
              onChange={(e) => setWorkshopContact(e.target.value)}
              className="w-full sm:w-1/2 border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400"
            />
          </div>

          <div className="bg-white border border-[#e2e1eb] rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[13.5px] font-semibold text-[#1a1b22]">Parking Charges</div>
                <div className="text-[11.5px] text-[#747684] mt-0.5">
                  Controls when parking charges become applicable and how the running estimate is calculated. This is an operational
                  estimate only — no invoice, billing or accounts entry is created automatically.
                </div>
              </div>
              <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#1a1b22] shrink-0 ml-3">
                <input
                  type="checkbox" data-testid="loc-tat-parking-enabled" checked={parkingEnabled}
                  onChange={(e) => setParkingEnabled(e.target.checked)}
                  className="w-4 h-4 accent-yamaha-700"
                />
                Enabled
              </label>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${parkingEnabled ? "" : "opacity-50 pointer-events-none"}`}>
              <div>
                <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
                  Charge / Day (₹)
                </label>
                <input
                  data-testid="loc-tat-parking-per-day" value={parkingPerDay}
                  onChange={(e) => setParkingPerDay(e.target.value)}
                  className="w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400"
                />
              </div>
              <div>
                <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
                  Grace Period (Days)
                </label>
                <input
                  type="number" min={0} data-testid="loc-tat-parking-grace-days" value={parkingGraceDays}
                  onChange={(e) => setParkingGraceDays(parseInt(e.target.value, 10) || 0)}
                  className="w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400"
                />
              </div>
              <div>
                <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
                  Charge Start Rule
                </label>
                <select disabled data-testid="loc-tat-parking-start-rule" value="loc2_generation_plus_grace"
                  className="w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] bg-[#f7f7fb] text-[#747684]">
                  <option value="loc2_generation_plus_grace">LOC 2 Generation Date + Grace Period</option>
                </select>
              </div>
            </div>
            <div className="text-[11px] text-[#9a9ba6] mt-2.5">
              When LOC 2 is generated, this grace period suggests the parking charge start date — the staff member generating LOC 2
              can review and edit that date before it is locked in for the cycle.
            </div>
          </div>
          <Block
            title="Vehicle Ready — Customer Not Taking Delivery"
            desc="Base date: Vehicle Ready Date"
            state={vehicleReady} setState={setVehicleReady} prefix="loc-tat-ready"
          />
          <Block
            title="No Work Started — Customer Not Responding"
            desc="Base date: LOC Trigger Date"
            state={noWorkStarted} setState={setNoWorkStarted} prefix="loc-tat-nowork"
          />
          <div className="flex justify-end">
            <button onClick={save} disabled={saving} data-testid="loc-tat-save-btn"
              className="px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-60">
              {saving ? "Saving…" : "Save LOC Settings"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;
